<script setup>
import { BAlert, BButton, BCol, BForm, BFormGroup, BFormTextarea, BLink, BModal, BRow } from 'bootstrap-vue-next';
import { computed, ref } from 'vue';

import { SUBMISSION_KIND_LABELS, SUBMISSION_KINDS, SUBMISSION_REVIEW_ACTIONS } from '~/common/Constants';
import { toLocaleDateStringIgnoringTimezone } from '~/common/DateUtils';
import { dateTimeFormatter } from '~/common/FieldFormats';
import { eventDetails } from '~/common/SubmissionDetails';

const TITLES = {
  [SUBMISSION_REVIEW_ACTIONS.approve]: 'Approve submission',
  [SUBMISSION_REVIEW_ACTIONS.reject]: 'Reject submission',
};

const props = defineProps({
  submission: {
    type: Object,
    required: true,
  },
  action: {
    type: String,
    required: true,
    validator: value => Object.values(SUBMISSION_REVIEW_ACTIONS).includes(value),
  },
  busy: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
});

const model = defineModel({ type: Boolean });
const emit = defineEmits(['approve', 'reject']);

const rejecting = computed(() => props.action === SUBMISSION_REVIEW_ACTIONS.reject);

const rejectReason = ref('');

const details = computed(() => {
  const submission = props.submission;
  const isEvent = submission.kind === SUBMISSION_KINDS.event;
  const startDate = toLocaleDateStringIgnoringTimezone(submission.occurred_at);

  return [
    {
      label: 'Submitted by',
      value: submission.username,
      to: { name: 'user', params: { userHandle: submission.username } },
    },
    { label: 'Type', value: SUBMISSION_KIND_LABELS[submission.kind] || submission.kind },
    { label: 'Name', value: submission.name },
    {
      label: isEvent ? 'Event date' : 'Publication date',
      value: submission.event_end_date
        ? `${startDate} to ${toLocaleDateStringIgnoringTimezone(submission.event_end_date)}`
        : startDate,
    },
    ...eventDetails(submission),
    { label: 'Link', value: submission.link, href: submission.link },
    { label: 'Note', value: submission.note },
    { label: 'Submitted', value: dateTimeFormatter(submission.created_at) },
  ];
});

const submitRejection = () => {
  emit('reject', rejectReason.value);
};

const resetForm = () => {
  rejectReason.value = '';
};
</script>

<template>
  <BModal
    v-model="model"
    :title="TITLES[action]"
    no-footer
    :no-close-on-backdrop="busy"
    :no-close-on-esc="busy"
    :no-header-close="busy"
    @hidden="resetForm"
  >
    <BAlert
      :model-value="!!error"
      variant="danger"
    >
      {{ error }}
    </BAlert>

    <BRow
      v-for="detail in details"
      :key="detail.label"
      class="mb-2"
    >
      <BCol sm="4">
        <strong>{{ detail.label }}</strong>
      </BCol>
      <BCol
        sm="8"
        class="text-break preserve-line-breaks"
      >
        <BLink
          v-if="detail.to"
          :to="detail.to"
        >
          {{ detail.value }}
        </BLink>
        <BLink
          v-else-if="detail.href"
          is-unsafe-link
          :href="detail.href"
          target="_blank"
        >
          {{ detail.value }}
        </BLink>
        <template v-else>{{ detail.value }}</template>
      </BCol>
    </BRow>

    <BForm
      v-if="rejecting"
      class="mt-4"
      @submit.prevent="submitRejection"
    >
      <BFormGroup
        label="Reason for rejection (shared with the submitter)"
        class="mb-3"
      >
        <BFormTextarea
          v-model="rejectReason"
          rows="3"
          required
        />
      </BFormGroup>
      <BButton
        type="submit"
        class="float-end"
        variant="danger"
        :loading="busy"
      >
        Reject
      </BButton>
    </BForm>
    <BButton
      v-else
      class="float-end mt-4"
      variant="success"
      :loading="busy"
      @click="emit('approve')"
    >
      Approve
    </BButton>
  </BModal>
</template>
